import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowDown, ArrowUp, Download, ExternalLink, GripVertical, Layers, Loader2, Plus, Presentation, Search, X,
} from "lucide-react";
import { pointApi, presentationApi } from "../../services/presentationApi";
import { BackLink, TitleField, ToolbarButton } from "../Offers/EditorChrome";
import ScaledSlide from "../Offers/proposal/ScaledSlide";
import SlideCanvas from "../Offers/proposal/SlideCanvas";
import SlideViewer from "../Offers/proposal/SlideViewer";

const day = (value) => (value ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");
const MONTH = () => new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
const SCOPE_NOUNS = { project: "Projet", client: "Client", programme: "Programme" };
const BACK = { backLabel: "Point client" };

const bodyParts = (parts) => parts.map(({ presentation_id: id, divider, label }) => ({ presentation_id: id, divider, label }));

// Nouveau point : un client (ou un programme), puis les présentations à réunir. Celles de chaque projet modifiées ces
// trois dernières semaines sont cochées d'office (la plus récente par projet).
export const NewAssemblyDialog = ({ onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [found, setFound] = useState({ clients: [], programmes: [] });
  const [scope, setScope] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [title, setTitle] = useState(`Point projets – ${MONTH()}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (scope) return undefined;
    const timer = setTimeout(() => {
      presentationApi.scopes(query).then(setFound).catch((err) => setError(err.message));
    }, 200);
    return () => clearTimeout(timer);
  }, [query, scope]);

  useEffect(() => {
    if (!scope) return;
    setCandidates(null);
    pointApi.candidates(scope.type, scope.id).then((data) => {
      setCandidates(data.items);
      setChosen(data.items.filter((item) => item.suggested).map((item) => item.id));
    }).catch((err) => setError(err.message));
  }, [scope]);

  const toggle = (id) => setChosen((value) => (value.includes(id) ? value.filter((item) => item !== id) : [...value, id]));

  const create = async (event) => {
    event.preventDefault();
    if (!scope || !title.trim()) return;
    setBusy(true);
    setError("");
    try {
      // Dans l'ordre de la liste (les plus récentes d'abord) ; l'ordre se règle ensuite.
      const ids = candidates.filter((item) => chosen.includes(item.id)).map((item) => item.id);
      const created = await pointApi.create({ scope_type: scope.type, scope_id: scope.id, title: title.trim(), presentation_ids: ids });
      navigate(`/tools/presentations/points/${created.assembly.id}`, { state: { backLabel: "Présentations" } });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const scopeOption = (type, item) => (
    <button key={`${type}-${item.id}`} type="button" onClick={() => setScope({ type, id: item.id, label: item.name })}
      className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50">
      <span className="truncate">{item.name}</span>
      <span className="shrink-0 text-xs text-slate-400">{type === "client" ? "client" : "programme"}</span>
    </button>
  );

  return (
    <div role="dialog" aria-modal="true" aria-label="Nouveau point client"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <form onSubmit={create} onClick={(event) => event.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-xl flex-col gap-4 rounded-xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Nouveau point client</h2>
          <button type="button" onClick={onClose} aria-label="Fermer" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <p className="-mt-2 text-sm text-slate-500">
          Les présentations de plusieurs projets réunies en une seule, sans être recopiées : chacune reste dans son projet,
          et ses retouches se voient dans le point.
        </p>
        {!scope ? (
          <div className="space-y-2">
            <span className="text-sm font-medium text-slate-700">Pour quel client ?</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Chercher un client ou un programme"
                className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-sm focus:border-blue-400 focus:outline-none" />
            </div>
            <div className="max-h-64 overflow-y-auto">
              {found.clients.map((item) => scopeOption("client", item))}
              {found.programmes.map((item) => scopeOption("programme", item))}
              {!found.clients.length && !found.programmes.length && <p className="px-2 text-sm text-slate-400">Aucun client trouvé.</p>}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 text-sm text-slate-600">
              <span>{SCOPE_NOUNS[scope.type]} : <strong className="text-slate-900">{scope.label}</strong></span>
              <button type="button" onClick={() => setScope(null)} className="text-blue-700 hover:underline">Changer</button>
            </div>
            <label className="block text-sm font-medium text-slate-700">
              Titre (couverture)
              <input value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
            </label>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
              <span className="text-sm font-medium text-slate-700">Présentations à réunir</span>
              {!candidates && <p className="text-sm text-slate-500">Chargement…</p>}
              {candidates && !candidates.length && (
                <p className="text-sm text-slate-500">Aucune présentation pour ce client : faites-les d'abord dans leurs projets.</p>
              )}
              {candidates?.map((item) => (
                <label key={item.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={chosen.includes(item.id)} onChange={() => toggle(item.id)} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-slate-900">{item.title}</span>
                    <span className="block truncate text-xs text-slate-500">
                      {SCOPE_NOUNS[item.scope_type]} · {item.scope_name} · {item.slide_count} slides · {day(item.updated_at)}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </>
        )}
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full px-4 py-2 text-sm text-slate-600 hover:bg-slate-100">Annuler</button>
          <button type="submit" disabled={busy || !scope || !title.trim() || !candidates}
            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            {busy ? "Création…" : `Créer le point${chosen.length ? ` (${chosen.length})` : ""}`}
          </button>
        </div>
      </form>
    </div>
  );
};

// Points client, les plus récents d'abord (Outils → Présentations).
export const AssemblyList = ({ reload }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => {
    pointApi.list().then((data) => setItems(data.items)).catch(() => setItems([]));
  }, [reload]);
  if (!items?.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="px-1 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Points client</div>
      <ul className="divide-y divide-slate-100">
        {items.map((item) => (
          <li key={item.id}>
            <button type="button" onClick={() => navigate(`/tools/presentations/points/${item.id}`, { state: { backLabel: "Présentations" } })}
              className="flex w-full items-center gap-3 px-1 py-2.5 text-left hover:bg-slate-50">
              <Layers className="h-4 w-4 shrink-0 text-blue-600" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-900">{item.title}</span>
                <span className="block truncate text-xs text-slate-500">
                  {item.scope_name} · {item.part_count} présentation{item.part_count > 1 ? "s" : ""}
                  {item.created_by_name ? ` · ${item.created_by_name}` : ""} · {day(item.updated_at)}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Une présentation du point : glissée pour changer l'ordre, intercalaire ou couverture d'origine, nom sur l'intercalaire.
const PartCard = ({ part, index, count, onMove, onChange, onRemove, onOpen, drag }) => {
  const [label, setLabel] = useState(part.label || "");
  useEffect(() => setLabel(part.label || ""), [part.label]);
  const commitLabel = () => {
    const next = label.trim() || null;
    if (next !== (part.label || null)) onChange({ label: next });
  };
  const button = "rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-30";
  return (
    <li draggable onDragStart={() => drag.start(index)} onDragOver={(event) => { event.preventDefault(); drag.over(index); }}
      onDrop={(event) => { event.preventDefault(); drag.drop(); }} onDragEnd={drag.end}
      className={`rounded-lg border bg-white p-2 shadow-sm ${drag.target === index && drag.from !== index ? "border-blue-400" : "border-slate-200"}`}>
      <div className="flex items-start gap-1.5">
        <GripVertical className="mt-0.5 h-4 w-4 shrink-0 cursor-grab text-slate-300" aria-hidden="true" />
        <span className="mt-0.5 w-6 shrink-0 text-sm font-semibold tabular-nums text-blue-600">{String(index + 1).padStart(2, "0")}</span>
        <div className="min-w-0 flex-1">
          {part.missing ? (
            <p className="flex items-center gap-1 text-sm text-rose-600">
              <AlertTriangle className="h-4 w-4" /> Présentation introuvable ou fermée
            </p>
          ) : (
            <>
              <button type="button" onClick={onOpen} title="Ouvrir la présentation dans son projet pour la modifier"
                className="block w-full truncate text-left text-sm font-medium text-slate-900 hover:text-blue-700">
                {part.title} <ExternalLink className="inline h-3 w-3 text-slate-400" />
              </button>
              <p className="truncate text-xs text-slate-500">{part.scope_label} · rév. {part.revision} · {day(part.updated_at)}</p>
              <label className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={part.divider} onChange={(event) => onChange({ divider: event.target.checked })} />
                Intercalaire à la place de la couverture
              </label>
              {part.divider && (
                <input value={label} onChange={(event) => setLabel(event.target.value)} onBlur={commitLabel}
                  onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }}
                  placeholder={part.default_label} aria-label="Nom sur l'intercalaire" maxLength={120}
                  className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-blue-400 focus:outline-none" />
              )}
            </>
          )}
        </div>
        <div className="flex shrink-0 flex-col">
          <button type="button" className={button} disabled={index === 0} onClick={() => onMove(index, index - 1)} aria-label="Monter">
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button type="button" className={button} disabled={index === count - 1} onClick={() => onMove(index, index + 1)} aria-label="Descendre">
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
          <button type="button" className={`${button} hover:text-rose-600`} onClick={onRemove} aria-label="Retirer du point" title="Retirer du point (la présentation reste dans son projet)">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </li>
  );
};

const moved = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

// Le point : à gauche l'ordre des présentations et la couverture, à droite l'aperçu de toutes les slides, présentation
// par présentation. On présente le point dans la fenêtre à part, comme une présentation ; on retouche une slide dans
// la présentation de son projet.
const PresentationAssembly = () => {
  const { assemblyId } = useParams();
  const id = Number(assemblyId);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragTarget, setDragTarget] = useState(null);

  const load = useCallback(() => pointApi.get(id).then(setData).catch((err) => setError(err.message)), [id]);
  useEffect(() => { load(); }, [load]);
  // Retour d'une présentation retouchée (autre onglet, fenêtre de présentation) : le point est relu.
  useEffect(() => {
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [load]);
  const scope = data?.assembly?.scope;
  useEffect(() => {
    if (scope) pointApi.candidates(scope.type, scope.id).then((found) => setCandidates(found.items)).catch(() => {});
  }, [scope?.type, scope?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data) document.title = data.assembly.title;
  }, [data]);

  const update = async (body, optimistic) => {
    if (optimistic) setData((value) => ({ ...value, ...optimistic(value) }));
    setBusy(true);
    setError("");
    try {
      setData(await pointApi.update(id, body));
    } catch (err) {
      setError(err.message);
      load();
    } finally {
      setBusy(false);
    }
  };
  const setParts = (parts) => update({ parts: bodyParts(parts) }, () => ({ parts }));
  const parts = useMemo(() => data?.parts || [], [data]);
  const move = (from, to) => setParts(moved(parts, from, to));
  const drag = {
    from: dragFrom, target: dragTarget,
    start: setDragFrom, over: setDragTarget,
    drop: () => { if (dragFrom != null && dragTarget != null && dragFrom !== dragTarget) move(dragFrom, dragTarget); },
    end: () => { setDragFrom(null); setDragTarget(null); },
  };
  const openPresentation = (presentationId) => navigate(`/tools/presentations/${presentationId}`, { state: BACK });

  const slides = useMemo(() => data?.deck?.slides || [], [data]);
  const pages = useMemo(() => Object.fromEntries(slides.map((slide, index) => [slide.id, index + 1])), [slides]);
  const groups = useMemo(() => {
    const result = [{ key: "intro", part: null, slides: [] }];
    slides.forEach((slide) => {
      const number = slide.source?.number;
      if (!number) result[0].slides.push(slide);
      else if (result[result.length - 1].part?.number === number) result[result.length - 1].slides.push(slide);
      else result.push({ key: `p${number}`, part: parts.find((part) => part.number === number), slides: [slide] });
    });
    return result;
  }, [slides, parts]);

  const exportPdf = async () => {
    setExporting(true);
    setError("");
    try {
      const { blob, filename } = await pointApi.pdf(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (error && !data) return <p className="p-6 text-sm text-rose-600">{error}</p>;
  if (!data) return <p className="p-6 text-sm text-slate-500">Chargement…</p>;
  const { assembly } = data;
  const included = new Set(parts.map((part) => part.presentation_id));
  const addable = candidates.filter((item) => !included.has(item.id));
  const field = "w-full rounded border border-slate-200 px-2 py-1 text-sm focus:border-blue-400 focus:outline-none";
  const viewed = viewing && slides.find((slide) => slide.id === viewing);

  const thumb = (slide) => (
    <div key={slide.id} className="group relative">
      <button type="button" onClick={() => setViewing(slide.id)} className="block w-full" title="Voir en grand">
        <ScaledSlide className="rounded border border-slate-200 bg-white shadow-sm transition group-hover:border-blue-300">
          <SlideCanvas slide={slide} meta={data.deck.meta} page={pages[slide.id]} embeds={data.deck.embeds}
            deckTitle={assembly.title} />
        </ScaledSlide>
      </button>
      <span className="pointer-events-none absolute bottom-1 left-1 rounded bg-white/90 px-1 text-[10px] tabular-nums text-slate-500">
        {pages[slide.id]}
      </span>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl space-y-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <BackLink to="/tools/presentations" label="Présentations" />
          <TitleField value={assembly.title} editable label="Titre du point" onCommit={(title) => update({ title })} />
          <p className="flex items-center gap-1.5 px-1 text-sm text-slate-500">
            <Layers className="h-4 w-4 text-blue-600" /> Point client · {scope.label} · {slides.length} slides
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <ToolbarButton onClick={exportPdf} disabled={exporting || !parts.length} title="PDF du point, imprimé par le serveur">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
          </ToolbarButton>
          <button type="button" disabled={!slides.length}
            onClick={() => window.open(`/presenter/points/${id}`, `deepflow-point-${id}`, "popup,width=1280,height=760")}
            title="Ouvrir le point dans une fenêtre à part, à partager en visio (flèches pour avancer, F pour le plein écran)"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-40">
            <Presentation className="h-4 w-4" /> Présenter
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <aside className="w-full shrink-0 space-y-4 lg:sticky lg:top-4 lg:w-80">
          <section className="space-y-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Ordre des présentations</h2>
            {!parts.length && <p className="text-sm text-slate-500">Aucune présentation : ajoutez-en ci-dessous.</p>}
            <ul className="space-y-2">
              {parts.map((part, index) => (
                <PartCard key={part.presentation_id} part={part} index={index} count={parts.length} drag={drag}
                  onMove={move} onOpen={() => openPresentation(part.presentation_id)}
                  onRemove={() => setParts(parts.filter((item) => item !== part))}
                  onChange={(patch) => setParts(parts.map((item) => (item === part ? { ...item, ...patch } : item)))} />
              ))}
            </ul>
            {adding ? (
              <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
                <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                  Présentations de {scope.name}
                  <button type="button" onClick={() => setAdding(false)} aria-label="Fermer" className="rounded p-0.5 hover:bg-slate-100"><X className="h-3.5 w-3.5" /></button>
                </div>
                {!addable.length && <p className="text-sm text-slate-500">Toutes ses présentations sont déjà dans le point.</p>}
                <div className="max-h-64 overflow-y-auto">
                  {addable.map((item) => (
                    <button key={item.id} type="button" onClick={() => { setParts([...parts, { presentation_id: item.id, divider: true, label: null }]); setAdding(false); }}
                      className="block w-full rounded px-2 py-1.5 text-left hover:bg-slate-50">
                      <span className="block truncate text-sm text-slate-900">{item.title}</span>
                      <span className="block truncate text-xs text-slate-500">{item.scope_name} · {day(item.updated_at)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)}
                className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm text-blue-700 hover:bg-blue-50">
                <Plus className="h-4 w-4" /> Ajouter une présentation
              </button>
            )}
          </section>
          <section className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Couverture</h2>
            <label className="block text-xs text-slate-600">Nature du document
              <input key={`k-${assembly.updated_at}`} defaultValue={assembly.kicker || ""} maxLength={80} className={field}
                onBlur={(event) => event.target.value !== (assembly.kicker || "") && update({ kicker: event.target.value })} />
            </label>
            <label className="block text-xs text-slate-600">Sous-titre
              <input key={`s-${assembly.updated_at}`} defaultValue={assembly.subtitle || ""} maxLength={300} className={field}
                placeholder="Facultatif"
                onBlur={(event) => event.target.value !== (assembly.subtitle || "") && update({ subtitle: event.target.value })} />
            </label>
            <label className="block text-xs text-slate-600">Date
              <input type="date" value={assembly.date || ""} className={field}
                onChange={(event) => event.target.value && update({ date: event.target.value })} />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={assembly.agenda} onChange={(event) => update({ agenda: event.target.checked })} />
              Sommaire après la couverture
            </label>
          </section>
          <button type="button" onClick={() => update({ archived: !assembly.archived })}
            className="text-xs text-slate-400 hover:text-slate-700 hover:underline">
            {assembly.archived ? "Désarchiver le point" : "Archiver le point"}
          </button>
        </aside>
        <main className="min-w-0 flex-1 space-y-5">
          {groups.map((group) => (group.slides.length > 0 && (
            <section key={group.key} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h2 className="truncate text-sm font-semibold text-slate-700">
                  {group.part ? `${String(group.part.number).padStart(2, "0")} · ${group.part.title}` : "Ouverture du point"}
                  {group.part && <span className="ml-2 font-normal text-slate-400">{group.part.scope_label}</span>}
                </h2>
                {group.part && (
                  <button type="button" onClick={() => openPresentation(group.part.presentation_id)}
                    className="inline-flex shrink-0 items-center gap-1 text-xs text-blue-700 hover:underline">
                    Modifier dans son projet <ExternalLink className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">{group.slides.map(thumb)}</div>
            </section>
          )))}
        </main>
      </div>
      {viewed && (
        <SlideViewer slides={slides} slideId={viewed.id} pages={pages} onSelect={setViewing} onClose={() => setViewing(null)}>
          <SlideCanvas slide={viewed} meta={data.deck.meta} page={pages[viewed.id]} embeds={data.deck.embeds} live
            deckTitle={assembly.title} />
        </SlideViewer>
      )}
    </div>
  );
};

export default PresentationAssembly;
